/**
 * «ANALIZANDO…» CON BARRA (Pablo, 28-09-2026): «que ponga "analizando", que después
 * cambie la frase, y que vayas viendo que está en proceso, una barra del 0 al 100 que
 * avance suave; que veas cuánto tarda».
 *
 * La IA no dice cuánto le falta, así que la barra es una ESTIMACIÓN honesta: sube rápido
 * al principio y se frena (1 − e^(−t/τ)), sin pasar del 95 % hasta que llega el resultado;
 * entonces llega al 100 y la ficha enseña el diagnóstico. Con los dos modelos a la vez el
 * análisis tarda ~15–20 s: a los 18 s va por el ~85 %. Cuenta desde que se PIDIÓ, que puede
 * ser en el formulario, antes de enviar: así no vuelve a empezar de 0 al abrir el parte.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { faseDelAnalisis, progresoEstimado } from "@/lib/pests/aiProgress";

export function AiAnalysisProgress({ since, done = false }: { since: number; done?: boolean }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (done) return;
    const h = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(h);
  }, [done]);
  const s = Math.max(0, (now - since) / 1000);
  const pct = done ? 100 : progresoEstimado(s);
  const fase = done ? "done" : faseDelAnalisis(s);
  return (
    <div
      className="p-3 rounded-xl border border-[#b6dded] bg-muted/30 space-y-2"
      data-testid="ai-analysis-progress"
      data-fase={fase}
      data-pct={pct}
    >
      <div className="flex items-center justify-between gap-2">
        {/* La frase cambia con un fundido: se ve que avanza, no que se ha colgado. */}
        <p key={fase} className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 animate-in fade-in slide-in-from-bottom-1 duration-500">
          <Sparkles className="w-4 h-4 text-primary shrink-0" />
          {t(`pests.scouting.aiProgress.${fase}`)}
        </p>
        <span className="text-xs tabular-nums text-muted-foreground shrink-0">
          {pct}% · {Math.floor(s)} s
        </span>
      </div>
      <div
        className="h-2 rounded-full bg-slate-200/80 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={t("pests.scouting.aiProgress.p1")}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
